import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';

class FavoritesService {
  constructor() {
    this.storageKey = 'chi_na_jian_favorites';
    this.favorites = this.loadFavorites();
  }

  loadFavorites() {
    try {
      const stored = localStorage.getItem(this.storageKey) || localStorage.getItem('restaurant_recommender_favorites');
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.filter(place => typeof place?.place_id === 'string' && place.place_id.trim()) : [];
    } catch {
      return [];
    }
  }

  saveFavorites() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.favorites));
  }

  serialize(place) {
    const location = place.geometry?.location;
    const number = value => typeof value === 'function' ? value() : value;
    const photo = place.photos?.[0] || place.details?.photos?.[0];
    return {
      place_id: place.place_id,
      name: place.name || place.details?.name,
      vicinity: place.vicinity || place.formatted_address || place.details?.formatted_address || '',
      rating: place.rating ?? place.details?.rating ?? null,
      user_ratings_total: place.user_ratings_total ?? place.details?.user_ratings_total ?? 0,
      price_level: place.price_level ?? place.details?.price_level ?? null,
      types: (Array.isArray(place.types) ? place.types : Array.isArray(place.details?.types) ? place.details.types : []).filter(type => typeof type === 'string'),
      opening_hours: place.opening_hours ? { open_now: place.opening_hours.open_now } : null,
      distance: place.distance ?? null,
      walkMinutes: place.walkMinutes ?? null,
      cuisineLabel: place.cuisineLabel || '餐飲',
      recommendationReasons: place.recommendationReasons || [],
      michelinGuide: place.michelinGuide || null,
      photoUrl: place.photoUrl || (typeof photo?.getUrl === 'function' ? photo.getUrl({ maxWidth: 900 }) : ''),
      geometry: location ? { location: { lat: number(location.lat), lng: number(location.lng) } } : null,
      addedAt: typeof place.addedAt === 'string' && Number.isFinite(Date.parse(place.addedAt)) ? place.addedAt : new Date().toISOString()
    };
  }

  addFavorite(place) {
    if (!place?.place_id || this.isFavorite(place.place_id)) return false;
    this.favorites = [this.serialize(place), ...this.favorites];
    this.saveFavorites();
    return true;
  }

  removeFavorite(placeId) {
    const next = this.favorites.filter(item => item.place_id !== placeId);
    if (next.length === this.favorites.length) return false;
    this.favorites = next;
    this.saveFavorites();
    return true;
  }

  isFavorite(placeId) {
    return this.favorites.some(item => item.place_id === placeId);
  }

  getFavorites() {
    return [...this.favorites];
  }

  clearFavorites() {
    this.favorites = [];
    this.saveFavorites();
  }

  async syncWithCloud(userId) {
    const ref = doc(db, 'users', userId);
    const snapshot = await getDoc(ref);
    const saved = snapshot.exists() ? snapshot.data().favorites : [];
    const remote = Array.isArray(saved) ? saved.filter(place => typeof place?.place_id === 'string' && place.place_id.trim()) : [];
    const merged = new Map([...remote, ...this.favorites].map(item => [item.place_id, this.serialize(item)]));
    this.favorites = [...merged.values()].sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    this.saveFavorites();
    await setDoc(ref, { favorites: this.favorites, updatedAt: serverTimestamp() }, { merge: true });
    return this.getFavorites();
  }

  async saveToCloud(userId) {
    await setDoc(doc(db, 'users', userId), {
      favorites: this.favorites,
      updatedAt: serverTimestamp()
    }, { merge: true });
  }
}

export default new FavoritesService();
